import { useEnvApi } from '../composables/useEnvApi'
import { useServerFetch } from '../composables/useServerFetch'
import { request } from './request'

/** 软件排行接口支持的终端类型：1 为 iOS，2 为 Android。 */
export type SoftwareDeviceType = 1 | 2

export type SoftwareRankingItem = {
  appBundleId: string
  marketId: number | string
  softwareId: string
  softwareType: string
  showPlatform?: number | string
  name: string
  logo: string
  title: string
  rating?: number | string
  downloadNum?: number | string
  activeValue?: number | string
  sizes?: number | string
  supportRegionCount?: number | string
  rankNo?: number
  isShowRank?: boolean
  traderCode?: string
  traderIcon?: string
  traderName?: string
  brandType?: number
}

/** 软件排行榜按全球和当前地区分别返回。 */
export type SoftwareRankingData = {
  global: SoftwareRankingItem[]
  current: SoftwareRankingItem[]
}

const SOFTWARE_RANKING_API_PATHS = {
  downloadRanking: '/api/v2/softwate/get_software_download_ranking_list',
  monthlyActiveRanking: '/api/v1/softwate/get_month_active',
} as const

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** 兼容 SSR、客户端和网关返回的 data 包装。 */
const unwrapResponse = (response: unknown): unknown => {
  let payload = response

  for (let depth = 0; depth < 4 && isRecord(payload); depth += 1) {
    if (payload.succeed === false || payload.success === false) {
      throw new Error('Software ranking API returned an unsuccessful response')
    }

    if ('data' in payload) {
      payload = payload.data
    } else if ('Data' in payload) {
      payload = payload.Data
    } else {
      break
    }
  }

  return payload
}

/** 只拆除网关响应包装，保留排行榜数组中的原始字段和顺序。 */
const normalizeSoftwareRankingData = (response: unknown): SoftwareRankingData => {
  const payload = unwrapResponse(response)

  if (!isRecord(payload) || (!Array.isArray(payload.global) && !Array.isArray(payload.current))) {
    throw new Error('Software ranking response is missing its global/current lists')
  }

  return {
    global: payload.global as SoftwareRankingItem[],
    current: payload.current as SoftwareRankingItem[],
  }
}

/** SSR 与客户端统一请求软件首页排行接口。 */
const requestSoftwareRankingData = (url: string, deviceType: SoftwareDeviceType): Promise<unknown> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikiStore : api.v4,
    url,
    method: 'GET' as const,
    params: { deviceType },
  }

  return import.meta.server ? useServerFetch(requestConfig) : request<unknown, unknown>(requestConfig)
}

/** 获取指定终端的月度下载排行。 */
export const getSoftwareDownloadRanking = async (deviceType: SoftwareDeviceType): Promise<SoftwareRankingData> => {
  const response = await requestSoftwareRankingData(SOFTWARE_RANKING_API_PATHS.downloadRanking, deviceType)
  return normalizeSoftwareRankingData(response)
}

/** 获取指定终端的月度活跃排行。 */
export const getSoftwareMonthlyActiveRanking = async (deviceType: SoftwareDeviceType): Promise<SoftwareRankingData> => {
  const response = await requestSoftwareRankingData(SOFTWARE_RANKING_API_PATHS.monthlyActiveRanking, deviceType)
  return normalizeSoftwareRankingData(response)
}
